import { useRef, useState } from 'react';
import {
  AdproductsUserBillingGetInvoicesApiHandler_scanPremiumPaymentHistory,
  type PremiumPaymentHistoryCursorDto,
  type RentalManagerPaymentTransactionDto,
} from '@zg-rentals/ts-adproducts-client';
import { LoadingConstants } from '@zg-rentals/rental-manager-env';
import { logError } from '@zg-rentals/log-error';

type PremiumPaymentHistoryState = {
  transactions: Array<RentalManagerPaymentTransactionDto>;
  hasMoreData: boolean;
  loadingState: string;
};

const initState = (): PremiumPaymentHistoryState => ({
  transactions: [],
  hasMoreData: false,
  loadingState: LoadingConstants.INITIAL,
});

export const usePremiumPaymentHistory = () => {
  const [state, setState] = useState<PremiumPaymentHistoryState>(initState());
  const cursorRef = useRef<PremiumPaymentHistoryCursorDto | undefined>(undefined);
  const loadingRef = useRef(false);

  const fetchNextPage = () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setState((prev) => ({ ...prev, loadingState: LoadingConstants.LOADING }));

    AdproductsUserBillingGetInvoicesApiHandler_scanPremiumPaymentHistory({ cursor: cursorRef.current })
      .then((result) => {
        loadingRef.current = false;
        cursorRef.current = result.cursor;
        return setState((prev) => {
          const seen = new Set(prev.transactions.map((t) => t.transactionId));
          const newTransactions = (result.transactions ?? []).filter((t) => {
            if (seen.has(t.transactionId)) return false;
            seen.add(t.transactionId);
            return true;
          });
          return {
            transactions: prev.transactions.concat(newTransactions),
            hasMoreData: result.hasMoreData,
            loadingState: LoadingConstants.SUCCESS,
          };
        });
      })
      .catch((error) => {
        loadingRef.current = false;
        logError({ error, errorType: 'PremiumPaymentHistoryLoad' });
        setState((prev) => ({ ...prev, loadingState: LoadingConstants.ERROR }));
      });
  };

  const handleLoadMore = (isVisible: boolean) => {
    if (isVisible && state.hasMoreData && state.loadingState !== LoadingConstants.LOADING) {
      fetchNextPage();
    }
  };

  return {
    transactions: state.transactions,
    hasMoreData: state.hasMoreData,
    loadingState: state.loadingState,
    fetchNextPage,
    handleLoadMore,
  };
};
